import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, X, FileText } from 'lucide-react';
import { api } from '../../lib/api';

interface JournalLine {
  id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  departmentId: string | null;
  departmentName: string | null;
  debitAmount: string;
  creditAmount: string;
  description: string | null;
}

interface JournalDetail {
  id: string;
  journalNumber: string;
  journalDate: string;
  journalType: string;
  description: string | null;
  status: string;
  createdByName: string;
  approvedByName: string | null;
  createdAt: string;
  approvedAt: string | null;
  lines: JournalLine[];
}

export function JournalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: journal, isLoading, error } = useQuery({
    queryKey: ['journal', id],
    queryFn: () => api.get<JournalDetail>(`/api/journals/${id}`),
    enabled: !!id,
  });

  const approveMutation = useMutation({
    mutationFn: () => api.post(`/api/journals/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', id] });
    },
  });

  const voidMutation = useMutation({
    mutationFn: () => api.post(`/api/journals/${id}/void`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !journal) {
    return (
      <div className="text-center py-12">
        <p style={{ color: 'var(--text-secondary)' }}>仕訳が見つかりません</p>
        <button
          onClick={() => navigate('/journals')}
          className="mt-4 text-sm hover:underline"
          style={{ color: 'var(--accent-color)' }}
        >
          仕訳一覧に戻る
        </button>
      </div>
    );
  }

  const totalDebit = journal.lines.reduce((sum, line) => sum + parseFloat(line.debitAmount || '0'), 0);
  const totalCredit = journal.lines.reduce((sum, line) => sum + parseFloat(line.creditAmount || '0'), 0);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'posted':
        return <span className="badge badge-success">転記済</span>;
      case 'draft':
        return <span className="badge badge-warning">下書き</span>;
      case 'void':
        return <span className="badge badge-error">取消</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const getTypeName = (type: string) => {
    switch (type) {
      case 'normal':
        return '通常仕訳';
      case 'adjustment':
        return '決算整理仕訳';
      case 'closing':
        return '締切仕訳';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/journals')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft size={20} style={{ color: 'var(--text-primary)' }} />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              仕訳詳細
            </h1>
            <p className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
              {journal.journalNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {journal.status === 'draft' && (
            <>
              <button
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: 'var(--success)' }}
              >
                <Check size={16} />
                転記
              </button>
              <button
                onClick={() => voidMutation.mutate()}
                disabled={voidMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: 'var(--error)' }}
              >
                <X size={16} />
                取消
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 仕訳情報 */}
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText size={20} style={{ color: 'var(--accent-color)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              仕訳情報
            </h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>日付</span>
              <span className="font-mono" style={{ color: 'var(--text-primary)' }}>
                {journal.journalDate}
              </span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>種類</span>
              <span style={{ color: 'var(--text-primary)' }}>{getTypeName(journal.journalType)}</span>
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>ステータス</span>
              {getStatusBadge(journal.status)}
            </div>
            <div className="flex justify-between">
              <span style={{ color: 'var(--text-secondary)' }}>作成者</span>
              <span style={{ color: 'var(--text-primary)' }}>{journal.createdByName}</span>
            </div>
            {journal.approvedByName && (
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>承認者</span>
                <span style={{ color: 'var(--text-primary)' }}>{journal.approvedByName}</span>
              </div>
            )}
            {journal.description && (
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>摘要</span>
                <p className="mt-1" style={{ color: 'var(--text-primary)' }}>
                  {journal.description}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 仕訳明細 */}
        <div className="lg:col-span-2 card overflow-hidden">
          <div className="p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              仕訳明細
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table>
              <thead>
                <tr>
                  <th style={{ color: 'var(--text-primary)' }}>勘定科目</th>
                  <th style={{ color: 'var(--text-primary)' }}>部門</th>
                  <th className="text-right" style={{ color: 'var(--text-primary)' }}>借方</th>
                  <th className="text-right" style={{ color: 'var(--text-primary)' }}>貸方</th>
                  <th style={{ color: 'var(--text-primary)' }}>摘要</th>
                </tr>
              </thead>
              <tbody>
                {journal.lines.map((line) => (
                  <tr key={line.id}>
                    <td style={{ color: 'var(--text-primary)' }}>
                      <span className="font-mono text-xs mr-2" style={{ color: 'var(--text-secondary)' }}>
                        {line.accountCode}
                      </span>
                      {line.accountName}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{line.departmentName || '-'}</td>
                    <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                      {parseFloat(line.debitAmount) > 0
                        ? `¥${parseFloat(line.debitAmount).toLocaleString()}`
                        : '-'}
                    </td>
                    <td className="text-right font-mono" style={{ color: 'var(--text-primary)' }}>
                      {parseFloat(line.creditAmount) > 0
                        ? `¥${parseFloat(line.creditAmount).toLocaleString()}`
                        : '-'}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{line.description || '-'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: 'var(--table-header-bg)' }}>
                  <td colSpan={2} className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    合計
                  </td>
                  <td className="text-right font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                    ¥{totalDebit.toLocaleString()}
                  </td>
                  <td className="text-right font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                    ¥{totalCredit.toLocaleString()}
                  </td>
                  <td>
                    {totalDebit === totalCredit ? (
                      <span className="text-green-600 text-sm">✓ 貸借一致</span>
                    ) : (
                      <span className="text-red-600 text-sm">✗ 貸借不一致</span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
